import { useEffect, useState } from 'react'
import {
  fishState, penCounts, siteFlowPath,
  type FishState, type LiceDetail, type Me, type Pen, type PenCount,
  type SiteFlow,
} from '../api'
import { useApi } from '../useApi'
import { Link } from '../router'
import { Anleggsvisning } from '../components/Anleggsvisning'
import { Fisken, type Ark } from '../components/Fisken'
import { Merdtelling } from '../components/Merdtelling'
import { Syklus } from '../components/Syklus'
import { isoUke } from '../components/Varsel'
import { useT } from '../lang'

/* The farm, the whole screen.
 *
 * WHY THIS IS AN ADDRESS AND NOT A BUTTON THAT EXPANDS A CARD.
 *
 * A Norwegian array is three hundred metres of diagonal, and a bounding box
 * round a diagonal is mostly sea. In a 460px card at Ljøsøy the seven rings
 * came out the size of a fingernail with the fjord doing nothing on either
 * side of them — which is a picture of the farm that cannot be worked on, only
 * glanced at. Everything the map is FOR — deciding which ring to treat, moving
 * fish across the array — needs the ring to be the size of a thumb.
 *
 * An address rather than a mode because of where it gets used. This is the
 * screen that stands on the wall in the fôrflåte all day, that gets sent in a
 * message as "look at merd 3", that somebody opens on a phone on the walkway
 * and reloads when the signal comes back. None of those survive a state that
 * exists only in one tab's memory.
 *
 * WHAT IS NOT HERE. The forecast and the history. They are the other two
 * horizons and they are tables — a week strip is not better full screen, and
 * putting them behind this door would make it a second copy of the farm page
 * rather than the map. This screen is one question: what is standing where,
 * right now, and what am I doing about it.
 */
export function Kart({ id, me }: { id: string; me: Me }) {
  const t = useT()
  const site = useApi<{ name: string; municipality: string | null;
                        locality_no: number | null }>(
    `/v1/sites/${encodeURIComponent(id)}`)
  const lus = useApi<LiceDetail>(`/v1/sites/${encodeURIComponent(id)}/lice`)
  const pens = useApi<{ items: Pen[] }>(
    `/v1/sites/${encodeURIComponent(id)}/pens`)

  /* THE CURRENT FORECAST. Polled at the upstream's own cadence: marine.earth.gl
     publishes Cache-Control max-age=300 and every layer below honours it, so
     asking more often cannot produce a fresher answer.

     A 403 here is the ordinary state for a farm that has not bought the
     forecast, and `useApi` reports it as an error the render simply does not
     draw. The map is unchanged and nothing says anything, which is right: an
     empty state for a product nobody bought is an advertisement. */
  const stroem = useApi<SiteFlow>(siteFlowPath(id, 48), { poll: 300_000 })

  const [valgtMerd, setValgtMerd] = useState<string | null>(null)
  const [fisk, setFisk] = useState<FishState | null>(null)
  const [ark, setArk] = useState<Ark | null>(null)
  const [armFrom, setArmFrom] = useState<string | null>(null)

  /* THE HOUR THE ARROWS ARE DRAWING: now, and only now.
     The forecast runs 48 hours and the scrubber that walked it lived in the
     side panel, which is gone -- this screen is the farm as it is, and the
     current belongs on the water rather than in a column of text beside it. */
  const flyt = stroem.data?.available ? stroem.data : null
  const timeIdx = flyt
    ? Math.max(0, flyt.hours.findIndex(
        (h) => new Date(h.time).getTime() >= Date.now() - 30 * 60_000))
    : 0
  const [telling, setTelling] = useState<{ pen: string | null } | null>(null)
  /* The panel of things that are not the map. Open by default on a desktop
     and closed on a phone would be two behaviours to reason about, so it is
     one: open, and a button folds it away when somebody wants the whole
     fjord. */
  const [panel, setPanel] = useState(true)

  const dag = lus.data?.own_counts?.[0]?.counted_at ?? null

  /* THE WEEK THE MAP IS SHOWING.
     Null means now, which is not the same as "the newest week on the ribbon":
     a farm that has not counted for a fortnight is at NOW and its newest week
     is a fortnight old, and conflating those two would make the live map
     silently a historical one. */
  const [tilbake, setTilbake] = useState<string | null>(null)
  const viser = tilbake ?? dag
  const historisk = tilbake !== null && tilbake !== dag

  const [merdrader, setMerdrader] = useState<PenCount[] | null>(null)
  useEffect(() => {
    if (!viser) { setMerdrader(null); return }
    let dead = false
    void penCounts(id, viser)
      .then((r) => { if (!dead) setMerdrader(r.items) })
      .catch(() => { if (!dead) setMerdrader([]) })
    return () => { dead = true }
  }, [id, viser])

  /* WHO STOOD WHERE THAT WEEK. Fetched here rather than inside `Fisken`,
     because `Fisken` is the panel you WORK in and it must go on describing
     now while the map is describing March. Two readers of one endpoint at two
     dates, which is the honest shape: the alternative is one state that is
     sometimes historical, and a move recorded against it lands in the wrong
     week. */
  const [fortid, setFortid] = useState<FishState | null>(null)
  useEffect(() => {
    if (!historisk || !tilbake) { setFortid(null); return }
    let dead = false
    void fishState(id, tilbake)
      .then((r) => { if (!dead) setFortid(r) })
      .catch(() => { if (!dead) setFortid(null) })
    return () => { dead = true }
  }, [id, tilbake, historisk])

  /* THE WHOLE SCREEN, and that includes the rail and the breadcrumb.
     Shell paints those; this asks it to stand down for as long as the map is
     mounted. A class on <body> rather than a prop threaded through four
     components, because what is being changed is the page and not a
     component. */
  useEffect(() => {
    document.body.classList.add('kartsiden')
    return () => document.body.classList.remove('kartsiden') }, [])

  const kanSkrive = me.can?.lice_count !== false
  const d = lus.data
  const counts = d?.own_counts ?? []
  /* The limit for the week ON SCREEN and not for the newest one. Scrub back
     into weeks 16–21 and the ladder tightens with the picture; reading
     today's limit against March's counts would paint an amber array green. */
  const viserGrense = viser && d ? grenseFor(d, viser) : null

  return (
    <div className="kartsiden-flate">
      {telling !== null && (
        <Merdtelling siteId={id} focusPen={telling.pen}
          initialDate={new Date().toISOString().slice(0, 10)}
          onSaved={(items) => { setMerdrader(items); setTelling(null) }}
          onClose={() => setTelling(null)} />
      )}

      {/* The only chrome the map keeps: who this is, and the way back.
          Everything else is on the map or in the panel. */}
      <header className="kartsiden-topp">
        <Link className="btn ghost sm"
          to={`/sites/${encodeURIComponent(id)}`}>{t('Close')}</Link>
        <div>
          <strong>{site.data?.name ?? id}</strong>
          {site.data?.locality_no && (
            <span className="faint">{' · '}{t('Locality')}{' '}
              {site.data.locality_no}</span>
          )}
          {viser && <span className="faint">{' · '}{t('Week')}{' '}
            {isoUke(viser)}</span>}
          {/* A neutral chip. `.chip.warn` is red, and red has exactly three
              places in this system -- the alive indicator, an attribution
              conflict, the hottest spectrogram bins. "You have scrubbed back"
              is a state, not an alarm. */}
          {historisk && <span className="chip"
            style={{ marginLeft: 8 }}>{t('Looking back')}</span>}
        </div>
        <button type="button" className="btn ghost sm"
          aria-expanded={panel}
          onClick={() => setPanel(!panel)}>
          {panel ? t('Hide the panel') : t('Show the panel')}
        </button>
      </header>

      <div className={`kartsiden-kropp${panel ? '' : ' bare-kart'}`}>
        <div className="kartsiden-kart">
          <Anleggsvisning siteId={id} counts={merdrader ?? []}
            siteValue={viser
              ? (counts.find((c) => c.counted_at === viser)?.adult_female
                 ?? counts[0]?.adult_female ?? null) : null}
            grense={viserGrense}
            selected={valgtMerd} onSelect={setValgtMerd}
            occupancy={(historisk ? fortid : fisk)?.occupancy}
            fyller
            sisteFlytt={historisk ? null : (fisk?.moves?.[0] ?? null)}
            /* NOTHING IS WRITTEN FROM A WEEK IN THE PAST. Dragging a badge on
               a map showing March would record a move today from a ring the
               fish are not standing in, and the picture would look exactly
               right while it did. So the gestures are simply absent while the
               playhead is back, and the panel says why. */
            onMoveFish={kanSkrive && !historisk
              ? (from, to) => setArk({ maal: 'pen', from, to }) : undefined}
            onDropTarget={kanSkrive && !historisk
              ? (from, kind) => setArk({ maal: kind as Ark['maal'], from,
                                         to: from }) : undefined}
            onArmFish={kanSkrive && !historisk
              ? (pen) => setArmFrom(armFrom === pen ? null : pen) : undefined}
            armedFrom={armFrom}
            showNames={new Set(((historisk ? fortid : fisk)?.occupancy ?? [])
              .map((o) => o.group_id)).size > 1}
            setupHref={me.is_staff !== false
              ? `/sites/${encodeURIComponent(id)}/oppsett` : undefined}
            /* Only while the playhead is on the present. The current forecast
               covers the next two days; drawing it over a March that has
               already happened would put a forecast on a record, which is the
               one juxtaposition this map must never make. */
            stroem={!historisk ? (flyt?.hours[timeIdx] ?? null) : null} />

          {/* THE ARC, UNDER THE ARRAY.
              The map is one week of a cycle that runs for twenty months, and
              until now the other seventy-nine weeks were reachable only as a
              table on another tab. Here they are the axis the map sits on:
              drag along it and the farm replays. */}
          {d && (
            <Syklus siteId={id}
              grenseFor={(iso) => grenseFor(d, iso)}
              moves={fisk?.moves ?? []}
              groups={fisk?.groups ?? []}
              /* The weeks ahead. The same series the Varsel tab draws as a
                 strip — read here as the right-hand half of the arc, because
                 nothing on a farm is decided by what merd 3 did in March. */
              varsel={d.varsel && d.varsel.available !== false
                ? (d.varsel.timeseries ?? []) : []}
              valgt={viser}
              onVelg={(day) => { setTilbake(day); setArmFrom(null) }} />
          )}
        </div>

        {panel && (
          <aside className="kartsiden-panel">
            {/* Said where the next tap would be, not where the scrubbing is.
                Somebody who has dragged back six months and then reaches for
                a fish has to meet this before they meet a dead badge. */}
            {historisk && (
              <div className="notice">
                <strong>{t('You are looking at an earlier week.')}</strong>{' '}
                {t('Nothing can be changed from here — a move recorded now would be filed on the wrong week.')}
                <div className="row" style={{ marginTop: 10 }}>
                  <button className="btn sm" onClick={() => setTilbake(null)}>
                    {t('Back to now')}
                  </button>
                </div>
              </div>
            )}
            {!historisk && valgtMerd && kanSkrive && telling === null && (
              <button className="btn sm"
                onClick={() => setTelling({ pen: valgtMerd })}>
                {t('Count this pen')}
              </button>
            )}
            {!historisk && !valgtMerd && kanSkrive && telling === null && (
              <button className="btn primary sm"
                onClick={() => setTelling({ pen: null })}>
                {t('Register a count')}
              </button>
            )}
            {!historisk && (pens.data?.items.length ?? 0) > 0 && (
              <Fisken siteId={id} pens={pens.data!.items}
                canWrite={kanSkrive}
                onChanged={setFisk} ark={ark} setArk={setArk}
                armFrom={armFrom} setArmFrom={setArmFrom}
                valgtMerd={valgtMerd} onPlaced={() => setValgtMerd(null)} />
            )}
          </aside>
        )}
      </div>
    </div>
  )
}

/** The limit in force for the week a given date falls in. The same read as
 *  Farm.tsx makes, and for the same reason: 0,50 normally, 0,20 in weeks
 *  16–21, 0,10 there in a red production area — invisible today and wrong in
 *  April. */
function grenseFor(d: LiceDetail, iso: string): number | null {
  const want = isoUke(iso)
  for (const w of d.varsel?.timeseries ?? []) {
    if (w.data.uken && isoUke(w.time) === want) return w.data.uken.details.grense
  }
  return d.current?.threshold ?? null
}
